import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useContext, useState, useEffect } from "react";
import { getScore } from "../../api/ScoringApi";
import CopyButton from "../../components/CopyButton";
import FetchingSuggestions from "../../components/FetchingSuggestions";
import NavigationButtons from "../../components/NavigationButtons";
import Selection from "../../components/Selection";
import SwingRecording from "../../components/SwingRecording";
import { Context } from "../../context/FittingContext";
import WebSocketConnection from "../../utils/WebSocketConnection";
import { getClubSubTypeFilters } from "../../utils/ClubSubTypeMapper";

const ShaftSelection = ({ increaseStep, decreaseStep, trackAtStart, currentStep }) => {
  const [analyzing, setAnalyzing] = useState(!trackAtStart);
  const [scoringApiResults, setScoringApiResults] = useState([]);
  const [displayScoring, setDisplayScoring] = useState(false);
  const [selectingShaft, setSelectingShaft] = useState(!trackAtStart);
  const [analyzeError, setAnalyzeError] = useState(false);
  const [insufficientData, setInsufficientData] = useState(false);
  const { clubState, setSelectedShaft } = useContext(Context);
  var allowAnalyzeData = localStorage.getItem("sessionData") ? true : false;

  // When navigating back to shaft selection with existing session data, show selection view
  useEffect(() => {
    if (localStorage.getItem("sessionData") && currentStep === 1) {
      setAnalyzing(true);
      setSelectingShaft(true);
    }
  }, [currentStep]);

  const analyzeData = async () => {
    setAnalyzing(true);
    setInsufficientData(false);
    setAnalyzeError(false);
    WebSocketConnection.saveDataToLocal(
      clubState.selectedShaft.name,
      clubState.selectedHead.name
    );
    WebSocketConnection.captureData = false;
    var swingData = WebSocketConnection.formatDataForScoring();
    const clubType = localStorage.getItem("clubType") || "Drivers";
    const clubSubType = localStorage.getItem("clubSubType") || "";
    const clubSubTypes = getClubSubTypeFilters(clubSubType);
    const data = await getScore(
      {
        data: swingData,
      },
      true,
      clubState.selectedShaft.family,
      clubType,
      clubSubTypes
    ).catch(() => {
      setAnalyzeError(true);
    });
    if (data && data.length > 0 && data[0] && data[0].length > 0 && !analyzeError) {
      var formattedSuggestions = data[0].map((suggestion) => {
        if (suggestion.includes(" deg ")) {
          suggestion = suggestion.replace(" deg", "°");
        }
        return suggestion;
      });
      //save to local
      var sessionData = JSON.parse(localStorage.getItem("sessionData"));
      if (!sessionData.shaftSuggestions) {
        sessionData.shaftSuggestions = [];
      }
      sessionData.shaftSuggestions.push(formattedSuggestions);
      localStorage.setItem("sessionData", JSON.stringify(sessionData));

      var suggestions = {
        data: clubState.clubInfo.shafts.data
          .filter((club) => {
            return formattedSuggestions.includes(club.name);
          })
          .sort((a, b) => {
            var aIndex = formattedSuggestions.indexOf(a.name);
            var bIndex = formattedSuggestions.indexOf(b.name);
            return aIndex < bIndex ? -1 : 1;
          }),
        uniqueFamily: [],
        uniqueMan: [],
      };
      suggestions.data.forEach((club) => {
        if (!suggestions.uniqueFamily.includes(club.family)) {
          suggestions.uniqueFamily.push(club.family);
        }
        if (!suggestions.uniqueMan.includes(club.manufacturer)) {
          suggestions.uniqueMan.push(club.manufacturer);
        }
      });
      setScoringApiResults(suggestions);
      setDisplayScoring(true);
      setSelectingShaft(true);
    } else if (data && !analyzeError) {
      // API returned successfully but with empty results
      setInsufficientData(true);
      setDisplayScoring(true);
      setSelectingShaft(true);
    } else {
      setAnalyzeError(true);
    }
  };

  return (
    <>
      {!analyzing || selectingShaft ? (
        <NavigationButtons
          onBackClick={() => {
            decreaseStep();
            WebSocketConnection.saveDataToLocal(
              clubState.selectedShaft.name,
              clubState.selectedHead.name
            );
            WebSocketConnection.setOnMessageCallback(undefined);
            WebSocketConnection.captureData = false;
          }}
          onNextClick={() => {
            increaseStep();
            WebSocketConnection.saveDataToLocal(
              clubState.selectedShaft.name,
              clubState.selectedHead.name
            );
          }}
          stepPosition={currentStep}
        />
      ) : null}
      <Grid container sx={{ paddingX: 5, paddingTop: 1, paddingBottom: 5 }}>
        {analyzing ? (
          selectingShaft ? (
            <>
              <Grid item xs={12} sx={{ marginBottom: 1 }}>
                <Typography variant="h4">
                  Select a shaft from the suggestions or
                  continue with head selection
                </Typography>
                <Typography variant="subtitle1">
                  Suggestions are listed in order of likely fit for the user.
                  Note that some clubs may be relativly close in likely fit and
                  should not be discounted for not being listed as the most
                  likely fit.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Selection
                  currentSelection={clubState.selectedShaft}
                  onSelection={(selection) => setSelectedShaft(selection)}
                  tableRows={
                    displayScoring && insufficientData
                      ? []
                      : displayScoring
                      ? scoringApiResults.data
                      : clubState.clubInfo.shafts.data
                  }
                  famFilterOpts={
                    displayScoring && insufficientData
                      ? []
                      : displayScoring
                      ? scoringApiResults.uniqueFamily
                      : clubState.clubInfo.shafts.uniqueFamily
                  }
                  manFilterOpts={
                    displayScoring && insufficientData
                      ? []
                      : displayScoring
                      ? scoringApiResults.uniqueMan
                      : clubState.clubInfo.shafts.uniqueMan
                  }
                  onShowAll={
                    insufficientData || (scoringApiResults && scoringApiResults.data)
                      ? () => setDisplayScoring(!displayScoring)
                      : undefined
                  }
                  dataType="shaft"
                  insufficientData={displayScoring && insufficientData}
                />
                <Box
                  display="flex"
                  flexDirection="row-reverse"
                  sx={{ marginTop: 2 }}
                >
                  <Button
                    variant="contained"
                    onClick={() => {
                      setAnalyzing(false);
                      setScoringApiResults([]);
                      setDisplayScoring(false);
                      setSelectingShaft(false);
                      WebSocketConnection.captureData = true;
                    }}
                    sx={{ marginLeft: 2 }}
                  >
                    Record Swings
                  </Button>
                  <Button
                    variant="contained"
                    onClick={analyzeData}
                    sx={{ marginLeft: 2 }}
                    disabled={allowAnalyzeData ? false : true}
                  >
                    Analyze Data
                  </Button>
                  <CopyButton copyElements={[clubState.selectedShaft]} />
                </Box>
              </Grid>
            </>
          ) : analyzeError ? (
            <div>
              <Button
                variant="contained"
                onClick={() => {
                  window.location = window.location.origin;
                }}
                sx={{ marginLeft: 2 }}
                disabled={allowAnalyzeData ? false : true}
              >
                Go Back
              </Button>
              <Typography variant="h5" sx={{ color: "red" }}>
                There was an error retrieving results from the scoring API.
                Please take the following steps:
              </Typography>
              <Typography variant="h5" sx={{ color: "red" }}>
                1) Ensure that you are recording enough swings. We recommend at
                least 10.
              </Typography>
              <Typography variant="h5" sx={{ color: "red" }}>
                2) Click the "Initial Configuration" button above to be take
                back to the selection screen and start again.
              </Typography>
              <Typography variant="h5" sx={{ color: "red" }}>
                3) Check internet connection.
              </Typography>
              <Typography variant="h5" sx={{ color: "red" }}>
                4) If you have recorded enough swings and still see this issue
                then please contact an administrator. The scoring model may be
                out of date.
              </Typography>
            </div>
          ) : (
            <FetchingSuggestions />
          )
        ) : (
          <SwingRecording
            onAnalyze={analyzeData}
            shaftName={clubState.selectedShaft.name}
            headName={clubState.selectedHead.name}
          />
        )}
      </Grid>
    </>
  );
};

export default ShaftSelection;
